import { useCallback, useEffect, useState } from 'react'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
// The platform's auto-bet glyph — the same SyncIcon the chip-placement
// panel's auto button wears, so the two games read as one product.
import SyncIcon from 'src/app/assets/icons/ico-sync-alt.svg'
// v12.2 (tweak 1): the cash-out toggle's glyph — Font Awesome Pro 6.7.1
// "money-from-bracket" (owner-licensed). The wordmark is gone (long labels
// in other locales overflowed the pill); the localized "Cashout" lives on
// as aria-label + tooltip, and the VARIANT carries the state: duotone =
// OFF, solid = ON — the icon is its own state dot.
import CashoutOffIcon from 'src/app/assets/icons/ico-money-from-bracket-duotone.svg'
import CashoutOnIcon from 'src/app/assets/icons/ico-money-from-bracket-solid.svg'
import {
  resolveCashoutInputMode,
  resolveRidingFooterSlot,
  type CashoutControlPresence
} from './crashChipLogic'

// One full icon turn per toggle (mirrors chip-placement's action-spin beat).
const AUTO_SPIN_MS = 550

const MIN_MULTIPLIER = 1.01
const MAX_MULTIPLIER = 100
const STEP = 0.1

type Props = Readonly<{
  autoEnabled: boolean
  cashoutEnabled: boolean
  cashoutMultiplier: number
  // The v8 presence resolution (pure, tested in crashChipLogic.test.ts):
  //   editable — full instrument, toggle + stepper alive.
  //   locked   — bet riding WITH cash-out: frozen at full contrast, lock
  //              glyph + the riding terms, no editing.
  //   hidden   — bet riding with cash-out OFF: the pill doesn't render
  //              (no lock, no ghost) — the auto icon seats in the compact
  //              v10 pill chassis instead so the row still reads designed.
  presence: CashoutControlPresence
  /** Betting closes in the final seconds — the instrument warms amber to
   * warn "these terms are about to lock" (v34, matches the lamp). */
  closingSoon?: boolean
  onToggleAuto: () => void
  onToggleCashout: () => void
  onSetCashoutMultiplier: (multiplier: number) => void
  /** Tap on the LOCKED instrument's terms — explains why editing is off
   * ("Auto cash-out is locked for this round") instead of ignoring the tap
   * silently (v34 — the silent ignore read as "it skipped my change"). */
  onLockedTap?: () => void
  /** v41 — while locked, the NEXT round's cash-out draft is OFF: the
   * instrument turns amber ("still on this round, off next round"). */
  nextRoundCashoutOff?: boolean
}>

/**
 * Per-zone footer controls (v8): the platform's auto-bet ICON toggle plus
 * ONE cohesive cash-out instrument — a slim pill group
 * [cashout-icon | − 1.70x +] — instead of the old three stray buttons.
 *
 *   - Auto: round icon button (SyncIcon), green-armed when ON (white ink,
 *     v12.2 tweak 3), one full spin per tap — the exact visual language of
 *     the chip-placement panel's auto control. Always interactive (it
 *     governs the NEXT round's re-bet).
 *   - Cashout (v12.2 tweak 1): the toggle segment is an ICON — the FA
 *     money-from-bracket glyph, duotone when OFF, solid on the green fill
 *     (white ink) when ON; the localized "Cashout" survives as aria-label
 *     + tooltip (the auto icon's pattern). The stepper wakes with the
 *     toggle. Tabular numerals; the value never editable while riding.
 */
export function CrashZoneControls({
  autoEnabled,
  cashoutEnabled,
  cashoutMultiplier,
  presence,
  closingSoon = false,
  onToggleAuto,
  onToggleCashout,
  onSetCashoutMultiplier,
  onLockedTap,
  nextRoundCashoutOff = false
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const { playEffect } = useSoundEffect()
  const [inputValue, setInputValue] = useState(cashoutMultiplier.toFixed(2))
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [spinAt, setSpinAt] = useState(0)

  // v12.3 (tweak 5): on coarse-pointer devices the multiplier field is a
  // read-only DISPLAY — focusing a free-text input pops the virtual
  // keyboard and iOS focus-zooms into the pill — and the −/+ steppers are
  // the only editor there. Desktop keeps free typing. Sampled once per
  // mount: a device's primary pointer doesn't change mid-session (the
  // resolution itself is pure + tested — resolveCashoutInputMode).
  const [coarsePointer] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches
  )
  const inputMode = resolveCashoutInputMode(coarsePointer)

  const editable = presence === 'editable'
  // v9 chassis (reshaped v10): the footer row always renders SOMETHING
  // designed — the full pill, the locked pill, or the auto icon seated in
  // a compact quiet chassis (riding with cash-out OFF) — so the auto icon
  // never floats alone under the card.
  const footerSlot = resolveRidingFooterSlot(presence)
  const seated = footerSlot === 'auto-seat'

  const handleToggleAuto = () => {
    playEffect('click')
    // Restart the spin cleanly so rapid taps re-fire it (reference pattern).
    setSpinAt(0)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setSpinAt(Date.now()))
    })
    onToggleAuto()
  }

  useEffect(() => {
    if (spinAt === 0) return
    const id = setTimeout(() => setSpinAt(0), AUTO_SPIN_MS)
    return () => clearTimeout(id)
  }, [spinAt])

  const handleToggleCashout = () => {
    // v41 — the toggle also works while LOCKED: it flips the NEXT round's
    // draft only (the riding terms are a submission-time snapshot the
    // server already holds) — the instrument turns amber and the zone
    // fires the explaining notice. Only 'hidden' (riding, cash-out off →
    // no instrument) has nothing to toggle.
    if (!editable && presence !== 'locked') return
    playEffect('click')
    onToggleCashout()
  }

  const handleMultiplierChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9.]/g, '')
    const decimalIndex = value.indexOf('.')
    if (decimalIndex !== -1) {
      value = value.slice(0, decimalIndex + 1) + value.slice(decimalIndex + 1).replace(/\./g, '')
    }
    setInputValue(value)
  }, [])

  const validateAndSetMultiplier = useCallback(
    (value: string) => {
      const numValue = parseFloat(value)
      if (isNaN(numValue) || numValue < MIN_MULTIPLIER) {
        onSetCashoutMultiplier(MIN_MULTIPLIER)
        setInputValue(MIN_MULTIPLIER.toFixed(2))
      } else if (numValue > MAX_MULTIPLIER) {
        onSetCashoutMultiplier(MAX_MULTIPLIER)
        setInputValue(MAX_MULTIPLIER.toFixed(2))
      } else {
        onSetCashoutMultiplier(numValue)
        setInputValue(numValue.toFixed(2))
      }
    },
    [onSetCashoutMultiplier]
  )

  const handleMultiplierBlur = useCallback(() => {
    // Coarse pointer: focus was bounced without ever editing — there is
    // nothing to validate, and re-committing the (possibly stale) draft
    // string could clobber a stepper-set value.
    if (inputMode.readOnly) return
    setIsInputFocused(false)
    validateAndSetMultiplier(inputValue)
  }, [inputMode.readOnly, inputValue, validateAndSetMultiplier])

  const handleMultiplierFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Coarse pointer: the field is display-only — bounce focus straight
    // back out so no caret, no keyboard and no iOS zoom ever engage.
    if (inputMode.readOnly) {
      e.currentTarget.blur()
      return
    }
    setIsInputFocused(true)
    setInputValue(cashoutMultiplier.toFixed(2))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
  }

  const stepMultiplier = (direction: 1 | -1) => {
    if (!editable || !cashoutEnabled) return
    playEffect('click')
    const next = Math.min(
      MAX_MULTIPLIER,
      Math.max(MIN_MULTIPLIER, cashoutMultiplier + direction * STEP)
    )
    onSetCashoutMultiplier(next)
    setInputValue(next.toFixed(2))
  }

  return (
    <div className="crash-zone-controls">
      {/* Riding with cash-out OFF: the auto icon relaxes from its sphere
          into a compact quiet capsule (same 34px height + chassis family as
          the instrument) — the row reads designed, never icon + bare line. */}
      <button
        type="button"
        className={`crash-zone-controls__auto ${seated ? 'crash-zone-controls__auto--seated' : ''} ${autoEnabled ? 'is-on' : ''} ${spinAt > 0 ? 'is-spinning' : ''}`}
        onClick={handleToggleAuto}
        title={t('Auto')}
        aria-label={t('Auto')}
        aria-pressed={autoEnabled}
      >
        <SyncIcon />
      </button>

      {!seated && (
        // The locked pill answers taps with the "locked for this round"
        // notice; the editable pill never intercepts (its own controls own
        // the clicks). onClick on the container catches the disabled
        // toggle + the terms span alike.
        <div
          className={`crash-cashout ${cashoutEnabled ? 'is-on' : ''} ${
            footerSlot === 'instrument-locked' ? 'crash-cashout--locked' : ''
          } ${editable && closingSoon ? 'crash-cashout--closing' : ''} ${
            presence === 'locked' && nextRoundCashoutOff ? 'crash-cashout--locked-ending' : ''
          }`}
        >
          <button
            type="button"
            className="crash-cashout__toggle"
            onClick={handleToggleCashout}
            disabled={!editable && presence !== 'locked'}
            aria-pressed={cashoutEnabled}
            title={t('Cashout')}
            aria-label={t('Cashout')}
          >
            {/* Duotone = OFF, solid = ON — the variant IS the state cue
                (the old ⦿ dot retired with the wordmark, v12.2). */}
            {cashoutEnabled ? <CashoutOnIcon /> : <CashoutOffIcon />}
          </button>
          <span className="crash-cashout__divider" aria-hidden />
          {presence === 'locked' ? (
            // The riding bet's terms — a fact, not an editor: lock + value.
            // Tapping the TERMS explains the round lock (v34); the toggle
            // beside them is live for the next-round flow (v41).
            <span className="crash-cashout__terms" onClick={onLockedTap}>
              <span className="crash-cashout__lock" role="img" aria-label={t('Locked')}>
                <svg viewBox="0 0 12 12" aria-hidden focusable="false">
                  <path
                    fillRule="evenodd"
                    d="M6 1a2.6 2.6 0 0 0-2.6 2.6V5H3a1.1 1.1 0 0 0-1.1 1.1v3.8A1.1 1.1 0 0 0 3 11h6a1.1 1.1 0 0 0 1.1-1.1V6.1A1.1 1.1 0 0 0 9 5h-.4V3.6A2.6 2.6 0 0 0 6 1Zm1.5 4V3.6a1.5 1.5 0 1 0-3 0V5h3Z"
                  />
                </svg>
              </span>
              <span className="crash-cashout__value crash-cashout__value--static">
                {cashoutMultiplier.toFixed(2)}x
              </span>
            </span>
          ) : (
            // v14 (mobile relayout): the −/value/+ triple is grouped so the
            // ≤768px stack can seat it as its OWN full-width row under the
            // [auto | cashout] pair (crash-chip-panel.scss splits the pill
            // with display: contents there). On desktop the group is an
            // inline flex run inside the pill — visually identical to the
            // ungrouped v12.4 layout.
            <div className="crash-cashout__editor">
              <button
                type="button"
                className="crash-cashout__step"
                onClick={() => stepMultiplier(-1)}
                disabled={!cashoutEnabled}
              >
                −
              </button>
              <input
                type="text"
                // v12.3 (tweak 5): coarse pointer → readOnly + inputMode
                // 'none' (no virtual keyboard, no iOS focus-zoom; steppers
                // only). Desktop stays freely typable ('decimal').
                inputMode={inputMode.inputMode}
                readOnly={inputMode.readOnly}
                className="crash-cashout__value"
                value={isInputFocused ? inputValue : `${cashoutMultiplier.toFixed(2)}x`}
                onChange={handleMultiplierChange}
                onFocus={handleMultiplierFocus}
                onBlur={handleMultiplierBlur}
                onKeyDown={handleKeyDown}
                disabled={!cashoutEnabled}
              />
              <button
                type="button"
                className="crash-cashout__step"
                onClick={() => stepMultiplier(1)}
                disabled={!cashoutEnabled}
              >
                +
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
